import React from 'react';
import { Button, Input, InputNumber, Select, Typography } from 'antd';

import { optionSearchFilter } from '../Input';
import { PLUS, TRASH } from '../icons';
import { MAX_RESPONSES, RESPONSE_KINDS, RESPONSE_STYLES, slugifyKey } from './notificationEditorModel';

const { Text } = Typography;

/**
 * Panel 7 — **What people can do about it**: the buttons that ride the
 * notification, in the app and in the email.
 *
 * They are a registry on the definition, not markup on a template, because the
 * same button has to render three ways — an in-app button, a real button in an
 * email, and one day an SMS keyword — and all three resolve to the same
 * `responses[]` entry. A kind this release does not build (a later version's,
 * or one authored through the API) renders read-only and is saved back exactly
 * as it came: somebody's stored button is not this editor's to drop.
 *
 * The sentence at the foot is not decoration. Mail scanners follow links —
 * Outlook's will fire any GET in the message — so an emailed button cannot be
 * the act itself; it opens a confirm page whose button POSTs. An author who
 * does not know that will write "Click to resolve" and be surprised, so the
 * panel says it where the buttons are made.
 */
export default function ResponseButtons({ responses = [], onChange, cardOptions = [] }) {
	const patch = (index, next) => onChange(responses.map((response, i) => (i === index ? { ...response, ...next } : response)));
	const full = responses.length >= MAX_RESPONSES;

	const addResponse = () => onChange([...responses, {
		uid: `response-${Date.now()}-${responses.length}`,
		key: '',
		label: '',
		kind: 'acknowledge',
		minutes: 15,
		cardId: '',
		style: 'default',
		unknown: null,
		raw: null,
	}]);

	return (
		<section className="form-panel notification-editor__panel" data-testid="notification-editor-responses">
			<div className="form-panel__kicker-row">
				<div className="micro-label form-panel__kicker">
					What people can do about it <span className="form-panel__count">{responses.length}</span>
				</div>
				{/* Never a bare disabled control: it carries its reason beside
				    it, the way the ladder's "Add a rung" does. The sentence is
				    the API's own — the 400 an author would otherwise meet at
				    save, said where the button is made instead. */}
				<div className="notification-responses__add">
					{full ? (
						<Text type="secondary">
							{`That is all ${MAX_RESPONSES}. More answers than that is a form, and a form belongs on a record.`}
						</Text>
					) : null}
					<Button size="small" icon={<PLUS />} onClick={addResponse} disabled={full}>
						Add a button
					</Button>
				</div>
			</div>

			{responses.length ? (
				<div className="notification-responses">
					{responses.map((response, index) => (
						<div className="notification-responses__row" key={response.uid} data-testid={`notification-editor-response-${index}`}>
							{response.unknown ? (
								<Text type="secondary" className="notification-responses__unknown">
									{`"${response.label || response.key}" is a ${response.unknown.kind} button this editor does not draw. It is kept exactly as it is.`}
								</Text>
							) : (<>
								<Input
									className="notification-responses__label"
									aria-label={`Button ${index + 1} label`}
									placeholder="Label — e.g. I'm on it"
									value={response.label}
									onChange={event => patch(index, {
										label: event.target.value,
										// The key is the identity a response token is scoped
										// to, so it is derived once, on a button that has
										// never been saved, and then left alone: renaming a
										// live button must not orphan the links already in
										// somebody's mailbox.
										key: response.raw ? response.key : slugifyKey(event.target.value, ''),
									})}
								/>
								<Select
									className="notification-responses__kind"
									aria-label={`Button ${index + 1} does`}
									value={response.kind}
									onChange={kind => patch(index, { kind })}
									options={RESPONSE_KINDS}
								/>
								{response.kind === 'snooze' ? (
									<InputNumber
										className="notification-responses__minutes"
										aria-label={`Button ${index + 1} snooze minutes`}
										min={5}
										max={60 * 24}
										value={response.minutes}
										onChange={minutes => patch(index, { minutes: Number.isFinite(minutes) ? minutes : 15 })}
										addonAfter="min"
									/>
								) : null}
								{response.kind === 'create-item' ? (
									<Select
										className="notification-responses__card"
										aria-label={`Button ${index + 1} card`}
										placeholder="Which card"
										value={response.cardId || undefined}
										onChange={cardId => patch(index, { cardId })}
										options={cardOptions}
										showSearch
										filterOption={optionSearchFilter}
										popupClassName="card-picker-dropdown"
									/>
								) : null}
								<Select
									className="notification-responses__style"
									aria-label={`Button ${index + 1} style`}
									value={response.style}
									onChange={style => patch(index, { style })}
									options={RESPONSE_STYLES}
								/>
								<Button
									type="text"
									size="small"
									icon={<TRASH />}
									aria-label={`Remove button ${index + 1}`}
									onClick={() => onChange(responses.filter((unused, i) => i !== index))}
								/>
							</>)}
						</div>
					))}
				</div>
			) : (
				<Text type="secondary">No buttons — the notification is read, and that is all.</Text>
			)}

			<div className="form-panel__hint">
				These ride the email as real buttons. An emailed button opens a confirmation page
				first and acts only when it is pressed there, because mail scanners follow links —
				a button that acted on the click would be pressed by the scanner, not the person.
			</div>
		</section>
	);
}
